import React, { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SecurityCardsCarousel = ({ cards, speed = 0.8, onCardHover = null }) => {
  // Responsive card dimensions
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Card dimensions - responsive
  const cardWidth = isMobile ? 288 : 320; // w-72 on mobile, w-80 on desktop
  const gapWidth = isMobile ? 16 : 24;   // gap-4 on mobile, gap-6 on desktop
  const cardWithGap = cardWidth + gapWidth;
  const totalLength = cardWithGap * cards.length;

  // Start from middle copy to avoid initial blank space
  const initialOffset = -totalLength;

  const [offset, setOffset] = useState(initialOffset);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const dragRef = useRef(false);
  const lastXRef = useRef(0);
  const velRef = useRef(0);
  const autoScrollDirRef = useRef('left');
  const carouselRef = useRef(null);

  // Create extended array for seamless looping (4 copies for bidirectional)
  const extendedCards = [...cards, ...cards, ...cards, ...cards];

  // Detect when carousel is visible in viewport
  useEffect(() => {
    const currentCarousel = carouselRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1, // Trigger when 10% of the carousel is visible
      }
    );

    if (currentCarousel) {
      observer.observe(currentCarousel);
    }

    return () => {
      if (currentCarousel) {
        observer.unobserve(currentCarousel);
      }
    };
  }, []);

  // Animation loop with bidirectional logic - only runs when visible
  useEffect(() => {
    if (!isVisible || isAnimating || dragRef.current || hoveredCard !== null) return;

    let animationId = null;
    const minOffset = -totalLength * 3; // End of 4 copies
    const maxOffset = 0; // Beginning

    const animate = () => {
      setOffset(prev => {
        let newOffset = prev;

        // Moving left by default
        if (autoScrollDirRef.current === 'left') {
          newOffset = prev - speed;

          // When we reach the minimum (end of copy 4), reverse direction
          if (newOffset < minOffset) {
            autoScrollDirRef.current = 'right';
            newOffset = minOffset + speed;
          }
        } else {
          // Moving right
          newOffset = prev + speed;

          // When we reach the maximum (beginning), reverse direction
          if (newOffset > maxOffset) {
            autoScrollDirRef.current = 'left';
            newOffset = maxOffset - speed;
          }
        }

        return newOffset;
      });
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [isVisible, isAnimating, hoveredCard, speed, totalLength]);

  const onPointerDown = (e) => {
    // Don't start drag if clicking on buttons
    if (e.target.closest('button')) return;
    dragRef.current = true;
    lastXRef.current = e.clientX;
    velRef.current = 0;
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    velRef.current = dx;
    setOffset(prev => {
      let newOffset = prev + dx;
      // Keep within bounds during drag
      const maxOffset = 0;
      const minOffset = -totalLength * 3;
      return Math.max(minOffset, Math.min(maxOffset, newOffset));
    });
  };

  const onPointerUp = () => {
    dragRef.current = false;
  };

  const handleLeftClick = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setOffset(prev => prev - cardWithGap);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleRightClick = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setOffset(prev => prev + cardWithGap);
    setTimeout(() => setIsAnimating(false), 600);
  };

  return (
    <div
      ref={carouselRef}
      className="w-screen py-0 flex flex-col items-center justify-center cursor-grab active:cursor-grabbing bg-white -mx-[calc((100vw-100%)/2)]"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      style={{
        overflow: 'visible',
      }}
    >
      <div
        ref={containerRef}
        className="flex justify-center items-center"
        style={{
          width: 'calc(100vw - 2cm)',
          marginLeft: '1cm',
          marginRight: '1cm',
          overflow: 'visible',
          perspective: '1000px',
          paddingTop: '50px',
          paddingBottom: '50px',
        }}
      >
        {/* Carousel track with padding for hover overflow */}
        <div
          className="flex transition-transform"
          style={{
            transform: `translateX(${offset}px)`,
            transitionDuration: isAnimating ? '600ms' : '0ms',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            paddingTop: '24px',
            paddingBottom: '24px',
          }}
        >
          {extendedCards.map((card, index) => {
            const cardIndex = index % cards.length;
            const isHovered = hoveredCard === cardIndex;
            const opacity = hoveredCard !== null && !isHovered ? 0.4 : 1;

            return (
              <div
                key={`card-${index}`}
                className="flex-shrink-0 transition-all duration-300"
                style={{
                  opacity: opacity,
                  width: `${cardWidth}px`,
                  marginRight: `${gapWidth}px`,
                }}
                onMouseEnter={() => {
                  setHoveredCard(cardIndex);
                  onCardHover?.(cardIndex);
                }}
                onMouseLeave={() => {
                  setHoveredCard(null);
                  onCardHover?.(null);
                }}
              >
                <div
                  style={{
                    filter: isHovered ? 'drop-shadow(0 30px 60px rgba(6, 182, 212, 0.4)) drop-shadow(0 0 40px rgba(6, 182, 212, 0.3))' : 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transform: isHovered
                      ? (isMobile ? 'scale(1.05) translateY(-8px)' : 'scale(1.15) translateY(-16px)')
                      : 'scale(1) translateY(0)',
                  }}
                >
                  {card}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation arrows */}
      <div className="flex justify-center gap-2 -mt-8 relative z-50">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleLeftClick();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          disabled={isAnimating}
          className="p-1 rounded-full bg-cyan-500/20 hover:bg-cyan-500/40 transition-all duration-300 hover:scale-110 active:scale-95 disabled:opacity-50 cursor-pointer"
          aria-label="Scroll left"
        >
          <ChevronLeft size={16} className="text-cyan-400 pointer-events-none" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRightClick();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          disabled={isAnimating}
          className="p-1 rounded-full bg-cyan-500/20 hover:bg-cyan-500/40 transition-all duration-300 hover:scale-110 active:scale-95 disabled:opacity-50 cursor-pointer"
          aria-label="Scroll right"
        >
          <ChevronRight size={16} className="text-cyan-400 pointer-events-none" />
        </button>
      </div>
    </div>
  );
};

export default SecurityCardsCarousel;
